// Campaign Context
import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';
import { createCampaignModel, CAMPAIGN_STATUS, CAMPAIGN_TYPES } from '../data/campaignModels';
import { getActiveCampaignsForPosition, trackCampaignView, trackCampaignClick, hasUserClaimedCampaign } from '../utils/campaignUtils';

const CampaignContext = createContext();

export const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return context;
};

export const CampaignProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load All Campaigns (Admin)
   */
  const loadCampaigns = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const campaignsRef = collection(db, 'campaigns');
      let q = query(campaignsRef, orderBy('createdAt', 'desc'));

      // Apply filters
      if (filters.status) {
        q = query(campaignsRef, where('status', '==', filters.status), orderBy('createdAt', 'desc'));
      }
      if (filters.type) {
        q = query(campaignsRef, where('type', '==', filters.type), orderBy('createdAt', 'desc'));
      }
      if (filters.createdBy) {
        q = query(campaignsRef, where('createdBy', '==', filters.createdBy), orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      const campaignsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Update state directly
      setCampaigns(campaignsData);
      
      return campaignsData;
    } catch (err) {
      setError(err.message);
      console.error('Error loading campaigns:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load Campaigns for a Position (Frontend)
   */
  const loadCampaignsForPosition = async (positionId, language = 'th') => {
    try {
      setLoading(true);
      setError(null);

      const campaigns = await getActiveCampaignsForPosition(positionId, language);
      setCampaigns(campaigns);
      return campaigns;
    } catch (err) {
      setError(err.message);
      console.error('Error loading campaigns for position:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get Campaign by ID
   */
  const getCampaign = async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignSnap = await getDoc(campaignRef);
      
      if (!campaignSnap.exists()) {
        return null;
      }

      return {
        id: campaignSnap.id,
        ...campaignSnap.data()
      };
    } catch (err) {
      setError(err.message);
      console.error('Error getting campaign:', err);
      return null;
    }
  };

  /**
   * Create Campaign
   */
  const createCampaign = async (campaignData) => {
    try {
      setLoading(true);
      setError(null);

      const campaignModel = createCampaignModel({
        ...campaignData,
        createdBy: currentUser?.uid || null
      });

      const campaignsRef = collection(db, 'campaigns');
      const docRef = await addDoc(campaignsRef, campaignModel);

      await loadCampaigns();
      return { id: docRef.id, ...campaignModel };
    } catch (err) {
      setError(err.message);
      console.error('Error creating campaign:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update Campaign
   */
  const updateCampaign = async (campaignId, updates) => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      await loadCampaigns();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error updating campaign:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete Campaign
   */
  const deleteCampaign = async (campaignId) => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      await deleteDoc(campaignRef);

      await loadCampaigns();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error deleting campaign:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Approve Campaign
   */
  const approveCampaign = async (campaignId) => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        'approval.status': 'approved',
        'approval.reviewedBy': currentUser?.uid || null,
        'approval.reviewedAt': Timestamp.now(),
        status: CAMPAIGN_STATUS.APPROVED,
        updatedAt: Timestamp.now()
      });

      // Update local state immediately first
      setCampaigns(prevCampaigns => 
        prevCampaigns.map(c => 
          c.id === campaignId 
            ? { 
                ...c, 
                status: CAMPAIGN_STATUS.APPROVED,
                approval: {
                  ...c.approval || {},
                  status: 'approved',
                  reviewedBy: currentUser?.uid || null,
                  reviewedAt: Timestamp.now()
                },
                updatedAt: Timestamp.now()
              }
            : c
        )
      );
      
      // Then reload campaigns from database to ensure sync
      await loadCampaigns({});
      
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error approving campaign:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reject Campaign
   */
  const rejectCampaign = async (campaignId, rejectionReason) => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        'approval.status': 'rejected',
        'approval.reviewedBy': currentUser?.uid || null,
        'approval.reviewedAt': Timestamp.now(),
        'approval.rejectionReason': rejectionReason || '',
        status: CAMPAIGN_STATUS.REJECTED,
        updatedAt: Timestamp.now()
      });

      await loadCampaigns();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error rejecting campaign:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Approve Content Moderation
   */
  const approveModeration = async (campaignId) => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      
      // Get current campaign to check status
      const campaignSnap = await getDoc(campaignRef);
      const campaignData = campaignSnap.data();
      
      // Update moderation status
      const updateData = {
        'moderation.status': 'approved',
        'moderation.reviewedBy': currentUser?.uid || null,
        'moderation.reviewedAt': Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      // If campaign was pending approval and moderation is approved, change status to approved
      if (campaignData?.status === CAMPAIGN_STATUS.PENDING_APPROVAL || campaignData?.status === CAMPAIGN_STATUS.DRAFT) {
        updateData.status = CAMPAIGN_STATUS.APPROVED;
        updateData['approval.status'] = 'approved';
        updateData['approval.reviewedBy'] = currentUser?.uid || null;
        updateData['approval.reviewedAt'] = Timestamp.now();
      }
      
      await updateDoc(campaignRef, updateData);

      // Update local state immediately
      setCampaigns(prevCampaigns => 
        prevCampaigns.map(c => 
          c.id === campaignId 
            ? { 
                ...c, 
                status: updateData.status || c.status,
                moderation: {
                  ...c.moderation || {},
                  status: 'approved',
                  reviewedBy: currentUser?.uid || null,
                  reviewedAt: Timestamp.now()
                },
                approval: updateData['approval.status'] ? {
                  ...c.approval || {},
                  status: 'approved',
                  reviewedBy: currentUser?.uid || null,
                  reviewedAt: Timestamp.now()
                } : c.approval,
                updatedAt: Timestamp.now()
              }
            : c
        )
      );

      await loadCampaigns({});
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error approving moderation:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reject Content Moderation
   */
  const rejectModeration = async (campaignId, flags = [], notes = '') => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        'moderation.status': 'rejected',
        'moderation.reviewedBy': currentUser?.uid || null,
        'moderation.reviewedAt': Timestamp.now(),
        'moderation.flags': flags,
        'moderation.notes': notes,
        updatedAt: Timestamp.now()
      });

      await loadCampaigns();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error rejecting moderation:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Activate Campaign
   */
  const activateCampaign = async (campaignId) => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        status: CAMPAIGN_STATUS.ACTIVE,
        updatedAt: Timestamp.now()
      });

      await loadCampaigns();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error activating campaign:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Pause Campaign
   */
  const pauseCampaign = async (campaignId) => {
    try {
      setLoading(true);
      setError(null);

      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        status: CAMPAIGN_STATUS.PAUSED,
        updatedAt: Timestamp.now()
      });

      await loadCampaigns();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error pausing campaign:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Track Campaign View
   */
  const handleCampaignView = async (campaignId) => {
    try {
      await trackCampaignView(campaignId, currentUser?.uid || null);
    } catch (err) {
      console.error('Error tracking campaign view:', err);
    }
  };

  /**
   * Track Campaign Click
   */
  const handleCampaignClick = async (campaignId) => {
    try {
      await trackCampaignClick(campaignId, currentUser?.uid || null);
    } catch (err) {
      console.error('Error tracking campaign click:', err);
    }
  };

  /**
   * Check if User has Claimed Campaign
   */
  const checkUserClaimed = async (campaignId) => {
    if (!currentUser) return false;
    return await hasUserClaimedCampaign(campaignId, currentUser.uid);
  };

  const value = {
    campaigns,
    loading,
    error,
    loadCampaigns,
    loadCampaignsForPosition,
    getCampaign,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    approveCampaign,
    rejectCampaign,
    approveModeration,
    rejectModeration,
    activateCampaign,
    pauseCampaign,
    handleCampaignView,
    handleCampaignClick,
    checkUserClaimed
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};

export default CampaignProvider;

